// src/WODetailsPanel.js
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api";

/**
 * WO Details Panel (mobile/web)
 * Tabs (match desktop notebook concept):
 *  - Overview (read-only now)
 *  - Documents (upload/list/open)
 *  - Comments (add/list)
 *  - Labor / Parts / Cost / Close (we'll build next)
 */
export default function WODetailsPanel({ open, site, woNumber, onClose, onBack }) {
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!open) return;
    setTab("overview");
  }, [open]);

  if (!open) return null;

  return (
    <Modal title={`WO Details — ${woNumber || ""}`} onClose={onClose}>
      {/* ✅ NEW top bar */}
      <div className="flex items-center gap-2 mb-4">
        <button
          className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300"
          type="button"
          onClick={onBack}
        >
          ◀ Back
        </button>

        <div className="text-xs text-gray-500">
          Site: <span className="font-mono">{site || "-"}</span> • WO:{" "}
          <span className="font-mono">{woNumber || "-"}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        <TabBtn active={tab === "overview"} onClick={() => setTab("overview")}>Overview</TabBtn>
        <TabBtn active={tab === "docs"} onClick={() => setTab("docs")}>Documents</TabBtn>
        <TabBtn active={tab === "comments"} onClick={() => setTab("comments")}>Comments</TabBtn>
        <TabBtn active={tab === "labor"} onClick={() => setTab("labor")}>Book Labor</TabBtn>
        <TabBtn active={tab === "parts"} onClick={() => setTab("parts")}>Parts Tx</TabBtn>
        <TabBtn active={tab === "cost"} onClick={() => setTab("cost")}>WO Cost</TabBtn>
        <TabBtn active={tab === "close"} onClick={() => setTab("close")}>Close WO</TabBtn>
      </div>

      {tab === "overview" && <WOOverviewTab site={site} woNumber={woNumber} />}
      {tab === "docs" && <WODocumentsTab site={site} woNumber={woNumber} />}
      {tab === "comments" && <WOCommentsTab site={site} woNumber={woNumber} />}

      {tab === "labor" && <WOLaborTab site={site} woNumber={woNumber} />}
      {tab === "parts" && <WOPartsTab site={site} woNumber={woNumber} />}
      {tab === "cost" && <WOCostTab site={site} woNumber={woNumber} />}
      {tab === "close" && <Placeholder title="Close WO (next)" />}
    </Modal>
  );
}
function TabBtn({ active, onClick, children }) {
  return (
    <button
      className={`px-3 py-2 rounded ${active ? "bg-blue-600 text-white" : "bg-gray-200"}`}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}

function Placeholder({ title }) {
  return (
    <div className="text-sm text-gray-600">
      <div className="font-semibold text-gray-800 mb-2">{title}</div>
      We’ll build this tab next to match your desktop details window.
    </div>
  );
}

/* =========================
 * Overview Tab (WO Get)
 * ========================= */
function WOOverviewTab({ site, woNumber }) {
  const [wo, setWo] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // editable fields
  const [editAssignee, setEditAssignee] = useState("");
  const [editDue, setEditDue] = useState("");

  const [saving, setSaving] = useState(false);

  // assignee picker modal
  const [assigneeOpen, setAssigneeOpen] = useState(false);

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const qs = new URLSearchParams({ site, wo_number: woNumber });
      const r = await apiFetch(`/api/wo/get?${qs}`);
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setWo(data);

      // seed editable fields
      setEditAssignee(data?.assignee || "");
      setEditDue(String(data?.due_date || "").slice(0, 10));
    } catch (e) {
      setErr(String(e.message || e));
      setWo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!site || !woNumber) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, woNumber]);

  const dirty = useMemo(() => {
    if (!wo) return false;
    const a0 = (wo.assignee || "").trim();
    const d0 = String(wo.due_date || "").slice(0, 10);
    return a0 !== (editAssignee || "").trim() || d0 !== (editDue || "");
  }, [wo, editAssignee, editDue]);

  const saveBasic = async () => {
    if (!wo) return;
    setErr("");
    setSaving(true);
    try {
      const payload = {
        site,
        wo_number: woNumber,
        assignee: (editAssignee || "").trim() || null,
        due_date: (editDue || "").trim() || null,
      };

      const r = await apiFetch("/api/wo/update_basic", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(await r.text());

      await load();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-semibold">Overview</div>
        <div className="flex gap-2">
          <button
            className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300"
            onClick={load}
            type="button"
            disabled={loading || saving}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            className="px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            type="button"
            disabled={!dirty || saving || loading}
            onClick={saveBasic}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {err && <div className="text-red-600 mb-2">{err}</div>}

      {!wo ? (
        <div className="text-sm text-gray-600">No data loaded.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Info label="WO #" value={wo.wo_number} />
          <Info label="Status" value={wo.wo_status} />
          <Info label="Priority" value={wo.priority} />

          {/* ✅ Editable Due Date */}
          <div>
            <div className="text-gray-600 text-xs">Due Date</div>
            <input
              type="date"
              className="border rounded px-2 py-2 w-full bg-white"
              value={editDue || ""}
              onChange={(e) => setEditDue(e.target.value)}
            />
          </div>

          {/* ✅ Asset Code + Equipment Name */}
          <div className="md:col-span-2">
            <div className="text-gray-600 text-xs mb-1">Equipment</div>
            <div className="border rounded px-2 py-2 bg-white">
              <div className="font-mono">{wo.asset_code || "—"}</div>
              <div className="text-xs text-gray-600">
                {wo.equipment_name || "—"}
              </div>
            </div>
          </div>

          {/* ✅ Editable Assignee + picker */}
          <div className="md:col-span-2">
            <div className="text-gray-600 text-xs mb-1">Assignee</div>
            <div className="flex gap-2">
              <input
                className="border rounded px-2 py-2 w-full bg-white"
                value={editAssignee}
                readOnly
                placeholder="Select assignee…"
              />
              <button
                className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                type="button"
                onClick={() => setAssigneeOpen(true)}
              >
                🔍
              </button>
              <button
                className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                type="button"
                disabled={!editAssignee}
                onClick={() => setEditAssignee("")}
                title="Clear assignee"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="text-gray-600 text-xs mb-1">Description</div>
            <div className="border rounded p-2 bg-gray-50">{wo.description || "—"}</div>
          </div>

          <div className="md:col-span-2">
            <div className="text-gray-600 text-xs mb-1">Notes (initial issue note)</div>
            <div className="border rounded p-2 bg-gray-50 whitespace-pre-wrap">{wo.notes || "—"}</div>
          </div>
        </div>
      )}

      {/* ✅ Assignee picker (same behavior as WorkOrders.js picker) */}
      <AssigneePickerModal
        open={assigneeOpen}
        onClose={() => setAssigneeOpen(false)}
        site={site}
        onPick={(u) => setEditAssignee(u)}
        titleOverride="Select Assignee"
      />
    </div>
  );
}


function Info({ label, value }) {
  return (
    <div>
      <div className="text-gray-600 text-xs">{label}</div>
      <div className="border rounded px-2 py-2 bg-white">{value ?? "—"}</div>
    </div>
  );
}

/* =========================
 * Documents Tab
 * ========================= */
function WODocumentsTab({ site, woNumber }) {
  const [rows, setRows] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]); // File[]
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const qs = new URLSearchParams({ site, wo_number: woNumber });
      const r = await apiFetch(`/api/wo/docs/list?${qs}`);
      if (!r.ok) throw new Error(await r.text());
      setRows(await r.json());
    } catch (e) {
      setErr(String(e.message || e));
      setRows([]);
    }
  };

  useEffect(() => {
    if (!site || !woNumber) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, woNumber]);

  const upload = async () => {
    if (!pendingFiles.length) return;
    setErr("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("site", site);
      fd.append("wo_number", woNumber);
      fd.append("description", "WO Documents");
      for (const f of pendingFiles) fd.append("files", f);

      const r = await apiFetch("/api/wo/docs/upload", { method: "POST", body: fd });
      const ct = r.headers.get("content-type") || "";
      const body = ct.includes("application/json") ? await r.json().catch(() => null) : await r.text();

      if (!r.ok) {
        const msg =
          typeof body === "string"
            ? body
            : body?.detail
            ? JSON.stringify(body.detail)
            : JSON.stringify(body);
        throw new Error(msg || "Upload failed");
      }

      setPendingFiles([]);
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const openDoc = async (row) => {
    setErr("");
    try {
      const qs = new URLSearchParams({
        bucket: row.storage_bucket,
        path: row.storage_path,
        expires_in: "3600",
      });
      const r = await apiFetch(`/api/wo/docs/signed-url?${qs}`);
      if (!r.ok) throw new Error(await r.text());
      const out = await r.json();
      if (out?.url) window.open(out.url, "_blank", "noopener,noreferrer");
      else throw new Error("No URL returned");
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-semibold">Documents</div>
        <button className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={load} type="button">
          Refresh
        </button>
      </div>

      {err && <div className="text-red-600 mb-2">{err}</div>}

      {/* Upload row */}
      <div className="border rounded p-3 mb-3">
        <div className="font-semibold mb-2">Add WO Documents</div>
        <div className="flex flex-wrap gap-2">
          <label className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 cursor-pointer">
            Choose file(s)
            <input
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const raw = Array.from(e.target.files || []);
                e.target.value = "";
                if (!raw.length) return;
                setPendingFiles((prev) => [...prev, ...raw]);
              }}
            />
          </label>

          <button
            className="px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            type="button"
            disabled={busy || pendingFiles.length === 0}
            onClick={upload}
          >
            {busy ? "Uploading…" : "Upload"}
          </button>

          <button
            className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            type="button"
            disabled={busy || pendingFiles.length === 0}
            onClick={() => setPendingFiles([])}
          >
            Clear
          </button>
        </div>

        {pendingFiles.length > 0 && (
          <div className="mt-2 text-sm text-gray-700">
            <div className="font-semibold mb-1">Queued:</div>
            <ul className="list-disc pl-5">
              {pendingFiles.map((f, i) => (
                <li key={`${f.name}-${i}`}>{f.name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* List */}
      <div className="border rounded overflow-auto max-h-[55vh]">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {["Filename", "Type", "Size", "Uploaded", "By", ""].map((h) => (
                <th key={h} className="border p-2 bg-gray-100 text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.id ?? idx} className="hover:bg-gray-50">
                <td className="border p-2">{r.filename || ""}</td>
                <td className="border p-2">{r.kind || ""}</td>
                <td className="border p-2">{(r.size_bytes ?? 0).toLocaleString()}</td>
                <td className="border p-2">{String(r.uploaded_at || "").slice(0, 19)}</td>
                <td className="border p-2">{r.uploaded_by || ""}</td>
                <td className="border p-2">
                  <button className="px-2 py-1 rounded bg-blue-600 text-white" type="button" onClick={() => openDoc(r)}>
                    Open
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td className="border p-3 text-gray-600" colSpan={6}>No documents found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =========================
 * Comments Tab (needs backend endpoints)
 * ========================= */
function WOCommentsTab({ site, woNumber }) {
  const [rows, setRows] = useState([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const qs = new URLSearchParams({ site, wo_number: woNumber, limit: "500" });
      const r = await apiFetch(`/api/wo/comments/list?${qs}`);
      if (!r.ok) throw new Error(await r.text());
      setRows(await r.json());
    } catch (e) {
      setErr(String(e.message || e));
      setRows([]);
    }
  };

  useEffect(() => {
    if (!site || !woNumber) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, woNumber]);

  const add = async () => {
    const c = (text || "").trim();
    if (!c) return;
    setErr("");
    setBusy(true);
    try {
      const r = await apiFetch("/api/wo/comments/add", {
        method: "POST",
        body: JSON.stringify({ site, wo_number: woNumber, comment: c }),
      });
      if (!r.ok) throw new Error(await r.text());
      setText("");
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-semibold">Comments</div>
        <button className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={load} type="button">
          Refresh
        </button>
      </div>

      {err && <div className="text-red-600 mb-2">{err}</div>}

      <div className="border rounded p-3 mb-3">
        <div className="font-semibold mb-2">Add Comment</div>
        <textarea
          className="border rounded px-2 py-2 w-full"
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type comment…"
        />
        <div className="mt-2">
          <button
            className="px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            type="button"
            disabled={busy || !text.trim()}
            onClick={add}
          >
            {busy ? "Saving…" : "Log Comment"}
          </button>
        </div>
      </div>

      <div className="border rounded overflow-auto max-h-[55vh]">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {["When", "By", "Comment"].map((h) => (
                <th key={h} className="border p-2 bg-gray-100 text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.id ?? idx} className="hover:bg-gray-50">
                <td className="border p-2 whitespace-nowrap">{String(r.created_at || "").slice(0, 19)}</td>
                <td className="border p-2">{r.created_by || ""}</td>
                <td className="border p-2 whitespace-pre-wrap">{r.comment || ""}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td className="border p-3 text-gray-600" colSpan={3}>No comments yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function AssigneePickerModal({ open, onClose, site, onPick, titleOverride }) {
  const [company, setCompany] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [status, setStatus] = useState("Active");

  const [companyOptions, setCompanyOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function loadOptions() {
      try {
        const qs = new URLSearchParams({ site });
        const r = await apiFetch(`/api/pickers/users/options?${qs}`);
        const data = r.ok ? await r.json() : {};
        if (alive) setCompanyOptions(data?.companies || []);
      } catch {
        if (alive) setCompanyOptions([]);
      }
    }

    loadOptions();
    return () => {
      alive = false;
    };
  }, [open, site]);

  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function load() {
      setErr("");
      try {
        const qs = new URLSearchParams({
          site,
          company,
          contact,
          email,
          username,
          status,
        });
        const r = await apiFetch(`/api/pickers/users?${qs}`);
        const data = r.ok ? await r.json() : [];
        if (alive) setRows(data || []);
      } catch {
        if (alive) setErr("Failed to load users");
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [open, site, company, contact, email, username, status]);

  if (!open) return null;

  return (
    <Modal title={titleOverride || "Select Assignee"} onClose={onClose}>
      {err && <p className="text-red-600 mb-2">{err}</p>}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
        <select className="border rounded px-2 py-2" value={company} onChange={(e) => setCompany(e.target.value)}>
          <option value="">All Companies</option>
          {companyOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <input className="border rounded px-2 py-2" placeholder="Contact" value={contact} onChange={(e) => setContact(e.target.value)} />
        <input className="border rounded px-2 py-2" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input className="border rounded px-2 py-2" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} />

        <select className="border rounded px-2 py-2" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option>Active</option>
          <option>Deactive</option>
          <option>All</option>
        </select>
      </div>

      <div className="max-h-96 overflow-auto border rounded">
        {rows.map((r) => (
          <button
            key={r.id}
            className="w-full text-left px-3 py-2 border-b hover:bg-gray-50"
            type="button"
            onClick={() => {
              onPick(r.username || `${r.company} – ${r.contact_name}`);
              onClose();
            }}
          >
            <div className="font-semibold">{r.username || "(no username)"}</div>
            <div className="text-xs text-gray-600">{r.company} — {r.contact_name}</div>
            <div className="text-xs text-gray-600">{r.email}</div>
          </button>
        ))}
        {rows.length === 0 && <div className="p-3 text-sm text-gray-600">No users found.</div>}
      </div>
    </Modal>
  );
}

/* =========================
 * Labor Tab (wo_hours)
 * ========================= */
function WOLaborTab({ site, woNumber }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // modal
  const [open, setOpen] = useState(false);

  const totalHours = useMemo(() => {
    let t = 0;
    for (const r of rows) {
      const v = r.total_labor_hours ?? (Number(r.total_crew_labor_hours || 0) + Number(r.total_external_labor_hours || 0));
      t += Number(v || 0);
    }
    return t;
  }, [rows]);

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const qs = new URLSearchParams({ site, wo_number: woNumber, limit: "500" });
      const r = await apiFetch(`/api/wo/hours/list?${qs}`);
      if (!r.ok) throw new Error(await r.text());
      setRows((await r.json()) || []);
    } catch (e) {
      setErr(String(e.message || e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!site || !woNumber) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, woNumber]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="text-lg font-semibold">Booked Hours</div>

        <button
          className="ml-auto px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          type="button"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>

        <button
          className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
          type="button"
          onClick={() => setOpen(true)}
        >
          ⏱ Book Labor
        </button>
      </div>

      {err && <div className="text-red-600 mb-2">{err}</div>}

      <div className="border rounded overflow-auto max-h-[55vh]">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {["When", "User", "Hours", "By", "Comment"].map((h) => (
                <th key={h} className="border p-2 bg-gray-100 text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const intTxt = (r.internal_crew || "").trim();
              const extTxt = (r.external_repair_company || "").trim();
              const userCell = [intTxt ? `INT: ${intTxt}` : "", extTxt ? `EXT: ${extTxt}` : ""].filter(Boolean).join(" | ");

              const tot =
                r.total_labor_hours ??
                (Number(r.total_crew_labor_hours || 0) + Number(r.total_external_labor_hours || 0));

              return (
                <tr key={r.id ?? idx} className="hover:bg-gray-50">
                  <td className="border p-2 whitespace-nowrap">{String(r.created_at || "").slice(0, 19)}</td>
                  <td className="border p-2">{userCell}</td>
                  <td className="border p-2 text-center">{Number(tot || 0).toFixed(2)}</td>
                  <td className="border p-2 text-center">{r.created_by || ""}</td>
                  <td className="border p-2">{String(r.comment || "").replace(/\n/g, " ").slice(0, 140)}</td>
                </tr>
              );
            })}

            {rows.length === 0 && (
              <tr>
                <td className="border p-3 text-gray-600" colSpan={5}>No labor booked yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-right font-semibold">
        Total hours logged: {totalHours.toFixed(2)}
      </div>

      <BookLaborModal
        open={open}
        onClose={() => setOpen(false)}
        site={site}
        woNumber={woNumber}
        onSaved={async () => {
          setOpen(false);
          await load();
        }}
      />
    </div>
  );
}

function BookLaborModal({ open, onClose, site, woNumber, onSaved }) {
  const [tab, setTab] = useState("internal"); // internal | external
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // ✅ NEW: current user (prefill first internal row)
  const [meUsername, setMeUsername] = useState("");

  // rows
  const [internalRows, setInternalRows] = useState([{ name: "", hours: "" }]);
  const [externalRows, setExternalRows] = useState([{ company: "", hours: "" }]);
  const [comment, setComment] = useState("");

  // ✅ NEW: picker modal state
  const [pickUserOpen, setPickUserOpen] = useState(false);
  const [pickUserRowIdx, setPickUserRowIdx] = useState(-1);

  const [pickCompanyOpen, setPickCompanyOpen] = useState(false);
  const [pickCompanyRowIdx, setPickCompanyRowIdx] = useState(-1);

  const total = useMemo(() => {
    const sum = (arr, key) => arr.reduce((s, r) => s + (Number(r[key] || 0) || 0), 0);
    return sum(internalRows, "hours") + sum(externalRows, "hours");
  }, [internalRows, externalRows]);

  // ✅ Load /api/me once per open so we can prefill first internal row
  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        const r = await apiFetch("/api/me");
        if (!r.ok) return;
        const me = await r.json();
        const u = (me?.username || "").trim();
        if (u) setMeUsername(u);
      } catch {
        // ignore
      }
    })();
  }, [open]);

  // Reset modal state on open
  useEffect(() => {
    if (!open) return;

    setTab("internal");
    setBusy(false);
    setErr("");

    // internal first row defaults to signed-in user if we have it
    setInternalRows([{ name: meUsername || "", hours: "" }]);
    setExternalRows([{ company: "", hours: "" }]);
    setComment("");

    setPickUserOpen(false);
    setPickUserRowIdx(-1);
    setPickCompanyOpen(false);
    setPickCompanyRowIdx(-1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, meUsername]);

  if (!open) return null;

  const normalizeRows = () => {
    const ints = internalRows
      .map((r) => ({ name: (r.name || "").trim(), hours: Number(r.hours || 0) || 0 }))
      .filter((r) => r.name && r.hours > 0);

    const exts = externalRows
      .map((r) => ({ company: (r.company || "").trim(), hours: Number(r.hours || 0) || 0 }))
      .filter((r) => r.company && r.hours > 0);

    return { ints, exts };
  };

  const save = async () => {
    setErr("");
    const cmt = (comment || "").trim();
    const { ints, exts } = normalizeRows();

    // At least one labor entry OR comment
    if (!ints.length && !exts.length && !cmt) {
      setErr("Add at least one labor entry or a comment.");
      return;
    }

    // if name/company exists, hours must be > 0
    const missingInternal = internalRows
      .filter((r) => (r.name || "").trim() && !(Number(r.hours || 0) > 0))
      .map((r) => (r.name || "").trim());

    const missingExternal = externalRows
      .filter((r) => (r.company || "").trim() && !(Number(r.hours || 0) > 0))
      .map((r) => (r.company || "").trim());

    if (missingInternal.length || missingExternal.length) {
      const parts = [];
      if (missingInternal.length) parts.push(`Internal missing hours: ${missingInternal.join(", ")}`);
      if (missingExternal.length) parts.push(`External missing hours: ${missingExternal.join(", ")}`);
      setErr(parts.join(" • "));
      return;
    }

    setBusy(true);
    try {
      const r = await apiFetch("/api/wo/hours/add", {
        method: "POST",
        body: JSON.stringify({
          site,
          wo_number: woNumber,
          internal_entries: ints,
          external_entries: exts,
          comment: cmt || null,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      await onSaved?.();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const addInternal = () => setInternalRows((prev) => [...prev, { name: "", hours: "" }]);
  const addExternal = () => setExternalRows((prev) => [...prev, { company: "", hours: "" }]);

  const removeInternal = (idx) => setInternalRows((prev) => prev.filter((_, i) => i !== idx));
  const removeExternal = (idx) => setExternalRows((prev) => prev.filter((_, i) => i !== idx));

  const setInternalName = (idx, name) =>
    setInternalRows((prev) => prev.map((x, i) => (i === idx ? { ...x, name } : x)));

  const setExternalCompany = (idx, company) =>
    setExternalRows((prev) => prev.map((x, i) => (i === idx ? { ...x, company } : x)));

  return (
    <Modal title={`Book Labor — ${woNumber}`} onClose={onClose}>
      {err && <div className="text-red-600 mb-2">{err}</div>}

      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold">Total (hrs): {total.toFixed(2)}</div>
        <div className="flex gap-2">
          <button
            className={`px-3 py-2 rounded ${tab === "internal" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
            type="button"
            onClick={() => setTab("internal")}
          >
            Internal
          </button>
          <button
            className={`px-3 py-2 rounded ${tab === "external" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
            type="button"
            onClick={() => setTab("external")}
          >
            External
          </button>
        </div>
      </div>

      {/* INTERNAL TAB */}
      {tab === "internal" && (
        <div className="border rounded p-3 mb-3">
          <div className="font-semibold mb-2">Internal Labor</div>

          {internalRows.map((r, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2 items-center">
              <div className="md:col-span-8 flex gap-2">
                <input
                  className="border rounded px-2 py-2 w-full"
                  placeholder="User"
                  value={r.name}
                  onChange={(e) => setInternalName(idx, e.target.value)}
                />

                {/* ✅ NEW: per-row user picker */}
                <button
                  className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                  type="button"
                  onClick={() => {
                    setPickUserRowIdx(idx);
                    setPickUserOpen(true);
                  }}
                  title="Pick user"
                >
                  🔍
                </button>
              </div>

              <input
                className="border rounded px-2 py-2 md:col-span-3"
                placeholder="Hours"
                inputMode="decimal"
                value={r.hours}
                onChange={(e) => {
                  const v = e.target.value;
                  setInternalRows((prev) => prev.map((x, i) => (i === idx ? { ...x, hours: v } : x)));
                }}
              />

              <button
                className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 md:col-span-1"
                type="button"
                onClick={() => removeInternal(idx)}
                disabled={internalRows.length === 1}
                title={internalRows.length === 1 ? "At least one row" : "Remove row"}
              >
                ✖
              </button>
            </div>
          ))}

          <button className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300" type="button" onClick={addInternal}>
            ➕ Add Crew
          </button>
        </div>
      )}

      {/* EXTERNAL TAB */}
      {tab === "external" && (
        <div className="border rounded p-3 mb-3">
          <div className="font-semibold mb-2">External Labor</div>

          {externalRows.map((r, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2 items-center">
              <div className="md:col-span-8 flex gap-2">
                <input
                  className="border rounded px-2 py-2 w-full"
                  placeholder="Company"
                  value={r.company}
                  onChange={(e) => setExternalCompany(idx, e.target.value)}
                />

                {/* ✅ NEW: per-row external company picker */}
                <button
                  className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                  type="button"
                  onClick={() => {
                    setPickCompanyRowIdx(idx);
                    setPickCompanyOpen(true);
                  }}
                  title="Pick company"
                >
                  🔍
                </button>
              </div>

              <input
                className="border rounded px-2 py-2 md:col-span-3"
                placeholder="Hours"
                inputMode="decimal"
                value={r.hours}
                onChange={(e) => {
                  const v = e.target.value;
                  setExternalRows((prev) => prev.map((x, i) => (i === idx ? { ...x, hours: v } : x)));
                }}
              />

              <button
                className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 md:col-span-1"
                type="button"
                onClick={() => removeExternal(idx)}
                disabled={externalRows.length === 1}
                title={externalRows.length === 1 ? "At least one row" : "Remove row"}
              >
                ✖
              </button>
            </div>
          ))}

          <button className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300" type="button" onClick={addExternal}>
            ➕ Add Company
          </button>
        </div>
      )}

      <div className="border rounded p-3 mb-3">
        <div className="font-semibold mb-2">Comment (optional)</div>
        <textarea
          className="border rounded px-2 py-2 w-full"
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add note (optional)…"
        />
      </div>

      <div className="flex gap-2">
        <button
          className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          type="button"
          onClick={save}
          disabled={busy}
        >
          {busy ? "Saving…" : "Save Hours"}
        </button>

        <button className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" type="button" onClick={onClose}>
          Cancel
        </button>
      </div>

      {/* ✅ User picker modal (re-using your existing picker endpoints) */}
      <InternalUserPickerModal
        open={pickUserOpen}
        onClose={() => setPickUserOpen(false)}
        site={site}
        onPick={(username) => {
          if (pickUserRowIdx >= 0) setInternalName(pickUserRowIdx, username);
          setPickUserOpen(false);
          setPickUserRowIdx(-1);
        }}
      />

      {/* ✅ External company picker modal (re-using your existing endpoints) */}
      <ExternalCompanyPickerModal
        open={pickCompanyOpen}
        onClose={() => setPickCompanyOpen(false)}
        site={site}
        onPick={(rec) => {
          if (pickCompanyRowIdx >= 0) setExternalCompany(pickCompanyRowIdx, rec.company || "");
          setPickCompanyOpen(false);
          setPickCompanyRowIdx(-1);
        }}
      />
    </Modal>
  );
}

function InternalUserPickerModal({ open, onClose, site, onPick }) {
  const [company, setCompany] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [status, setStatus] = useState("Active");

  const [companyOptions, setCompanyOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function loadOptions() {
      try {
        const qs = new URLSearchParams({ site });
        const r = await apiFetch(`/api/pickers/users/options?${qs}`);
        const data = r.ok ? await r.json() : {};
        if (alive) setCompanyOptions(data?.companies || []);
      } catch {
        if (alive) setCompanyOptions([]);
      }
    }

    loadOptions();
    return () => {
      alive = false;
    };
  }, [open, site]);

  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function load() {
      setErr("");
      try {
        const qs = new URLSearchParams({ site, company, contact, email, username, status });
        const r = await apiFetch(`/api/pickers/users?${qs}`);
        const data = r.ok ? await r.json() : [];
        if (alive) setRows(data || []);
      } catch {
        if (alive) setErr("Failed to load users");
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [open, site, company, contact, email, username, status]);

  useEffect(() => {
    if (!open) return;
    setCompany("");
    setContact("");
    setEmail("");
    setUsername("");
    setStatus("Active");
    setRows([]);
    setErr("");
  }, [open]);

  if (!open) return null;

  return (
    <Modal title="Select Internal User" onClose={onClose}>
      {err && <p className="text-red-600 mb-2">{err}</p>}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
        <select className="border rounded px-2 py-2" value={company} onChange={(e) => setCompany(e.target.value)}>
          <option value="">All Companies</option>
          {companyOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <input className="border rounded px-2 py-2" placeholder="Contact" value={contact} onChange={(e) => setContact(e.target.value)} />
        <input className="border rounded px-2 py-2" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input className="border rounded px-2 py-2" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} />

        <select className="border rounded px-2 py-2" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option>Active</option>
          <option>Deactive</option>
          <option>All</option>
        </select>
      </div>

      <div className="max-h-96 overflow-auto border rounded">
        {rows.map((r) => (
          <button
            key={r.id}
            className="w-full text-left px-3 py-2 border-b hover:bg-gray-50"
            type="button"
            onClick={() => {
              onPick(r.username || `${r.company} – ${r.contact_name}`);
            }}
          >
            <div className="font-semibold">{r.username || "(no username)"}</div>
            <div className="text-xs text-gray-600">{r.company} — {r.contact_name}</div>
            <div className="text-xs text-gray-600">{r.email}</div>
          </button>
        ))}
        {rows.length === 0 && <div className="p-3 text-sm text-gray-600">No users found.</div>}
      </div>
    </Modal>
  );
}

function ExternalCompanyPickerModal({ open, onClose, site, onPick }) {
  const [company, setCompany] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function loadOptions() {
      try {
        const qs = new URLSearchParams({ site });
        const r = await apiFetch(`/api/pickers/external_companies/options?${qs}`);
        const data = r.ok ? await r.json() : {};
        if (alive) setCompanyOptions(data?.companies || []);
      } catch {
        if (alive) setCompanyOptions([]);
      }
    }

    loadOptions();
    return () => { alive = false; };
  }, [open, site]);

  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function load() {
      setErr("");
      try {
        const qs = new URLSearchParams({ site, company, limit: "300" });
        const r = await apiFetch(`/api/pickers/external_companies?${qs}`);
        const data = r.ok ? await r.json() : [];
        if (alive) setRows(data || []);
      } catch {
        if (alive) setErr("Failed to load companies");
      }
    }

    load();
    return () => { alive = false; };
  }, [open, site, company]);

  useEffect(() => {
    if (!open) return;
    setCompany("");
    setRows([]);
    setErr("");
  }, [open]);

  if (!open) return null;

  return (
    <Modal title="Select External Company" onClose={onClose}>
      {err && <p className="text-red-600 mb-2">{err}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        <select className="border rounded px-2 py-2" value={company} onChange={(e) => setCompany(e.target.value)}>
          <option value="">All Companies</option>
          {companyOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <input
          className="border rounded px-2 py-2"
          placeholder="Search company…"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
      </div>

      <div className="max-h-96 overflow-auto border rounded">
        {rows.map((r, idx) => (
          <button
            key={`${r.company}-${idx}`}
            className="w-full text-left px-3 py-2 border-b hover:bg-gray-50"
            type="button"
            onClick={() => onPick(r)}
          >
            <div className="font-semibold">{r.company}</div>
            <div className="text-xs text-gray-600">
              {r.contact_name || "—"} • {r.email || "—"} • {r.phone || "—"}
            </div>
          </button>
        ))}
        {rows.length === 0 && <div className="p-3 text-sm text-gray-600">No companies found.</div>}
      </div>
    </Modal>
  );
}


/* =========================
 * Parts Transaction Tab
 * ========================= */
function WOPartsTab({ site, woNumber }) {
  const [assetCode, setAssetCode] = useState("");
  const [status, setStatus] = useState("");

  // manual issue inputs
  const [pn, setPn] = useState("");
  const [qty, setQty] = useState("1");

  // modals
  const [searchOpen, setSearchOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  // issued inventory table
  const [invRows, setInvRows] = useState([]);
  const [invErr, setInvErr] = useState("");
  const [invBusy, setInvBusy] = useState(false);

  // non-inv table
  const [nonRows, setNonRows] = useState([]);
  const [nonErr, setNonErr] = useState("");
  const [nonBusy, setNonBusy] = useState(false);
  const [nonAddOpen, setNonAddOpen] = useState(false);

  // load WO hint fields (asset_code)
  useEffect(() => {
    if (!site || !woNumber) return;
    (async () => {
      try {
        const qs = new URLSearchParams({ site, wo_number: woNumber });
        const r = await apiFetch(`/api/wo/get?${qs}`);
        if (!r.ok) return;
        const wo = await r.json();
        setAssetCode((wo?.asset_code || "").trim());
      } catch {
        // ignore
      }
    })();
  }, [site, woNumber]);

  const refreshInv = async () => {
    setInvErr("");
    setInvBusy(true);
    try {
      const qs = new URLSearchParams({ site, wo_number: woNumber, limit: "500" });
      const r = await apiFetch(`/api/wo/parts/used?${qs}`);
      if (!r.ok) throw new Error(await r.text());
      setInvRows((await r.json()) || []);
    } catch (e) {
      setInvErr(String(e.message || e));
      setInvRows([]);
    } finally {
      setInvBusy(false);
    }
  };

  const refreshNon = async () => {
    setNonErr("");
    setNonBusy(true);
    try {
      const qs = new URLSearchParams({ site, wo_number: woNumber, limit: "500" });
      const r = await apiFetch(`/api/wo/noninv/list?${qs}`);
      if (!r.ok) throw new Error(await r.text());
      setNonRows((await r.json()) || []);
    } catch (e) {
      setNonErr(String(e.message || e));
      setNonRows([]);
    } finally {
      setNonBusy(false);
    }
  };

  useEffect(() => {
    if (!site || !woNumber) return;
    refreshInv();
    refreshNon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, woNumber]);

  const invTotals = useMemo(() => {
    let tq = 0;
    let tv = 0;
    for (const r of invRows) {
      tq += Number(r.part_quantity || 0) || 0;
      tv += Number(r.total_part_value || 0) || 0;
    }
    return { tq, tv };
  }, [invRows]);

  const nonTotals = useMemo(() => {
    let tq = 0;
    let tv = 0;
    for (const r of nonRows) {
      tq += Number(r.part_quantity || 0) || 0;
      tv += Number(r.total_value || 0) || 0;
    }
    return { tq, tv };
  }, [nonRows]);

  const issueManual = async () => {
    const part = (pn || "").trim();
    const q = Number(qty || 0);

    if (!part) return setStatus("Enter a Part #");
    if (!(q > 0)) return setStatus("Qty must be > 0");

    setStatus("");
    try {
      const r = await apiFetch("/api/wo/parts/issue", {
        method: "POST",
        body: JSON.stringify({
          site,
          wo_number: woNumber,
          part_number: part,
          qty_issued: q,
          asset_code: assetCode || null,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const out = await r.json();

      setStatus(`Issued ${out.issued} x ${out.part_number} (Batches: ${out.batch_details || "-"})`);
      setPn("");
      setQty("1");
      await refreshInv();
    } catch (e) {
      setStatus(`Issue failed: ${String(e.message || e)}`);
    }
  };

  return (
    <div>
      {/* Top actions */}
      <div className="border rounded p-3 mb-3">
        <div className="font-semibold mb-2">Issue Inventory Parts to WO</div>

        <div className="flex flex-wrap gap-2 items-center">
          <button
            className="px-3 py-2 rounded bg-orange-500 text-white hover:bg-orange-600"
            type="button"
            onClick={() => setSearchOpen(true)}
          >
            🔍 Search Inventory
          </button>

          <button
            className="px-3 py-2 rounded bg-purple-600 text-white hover:bg-purple-700"
            type="button"
            onClick={() => setScanOpen(true)}
          >
            📷 Scan QR
          </button>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Part #</span>
            <input
              className="border rounded px-2 py-2 w-44"
              value={pn}
              onChange={(e) => setPn(e.target.value)}
              placeholder="Scan or type…"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Qty</span>
            <input
              className="border rounded px-2 py-2 w-24"
              inputMode="decimal"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>

          <button
            className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            type="button"
            onClick={issueManual}
          >
            ➖ Issue to WO
          </button>

          <button
            className="ml-auto px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            type="button"
            onClick={refreshInv}
            disabled={invBusy}
          >
            {invBusy ? "Loading…" : "Refresh"}
          </button>
        </div>

        {status ? <div className="text-sm text-gray-700 mt-2">{status}</div> : null}
        {invErr ? <div className="text-red-600 mt-2">{invErr}</div> : null}
      </div>

      {/* Issued inventory table */}
      <div className="border rounded overflow-auto max-h-[45vh] mb-2">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {["When", "Part #", "Qty", "UOM", "Total Value", "Batch", "By"].map((h) => (
                <th key={h} className="border p-2 bg-gray-100 text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invRows.map((r, idx) => (
              <tr key={r.id ?? idx} className="hover:bg-gray-50">
                <td className="border p-2 whitespace-nowrap">{String(r.created_at || "").slice(0, 19)}</td>
                <td className="border p-2 font-mono">{r.part_number || ""}</td>
                <td className="border p-2 text-center">{Number(r.part_quantity || 0).toFixed(2)}</td>
                <td className="border p-2 text-center">{r.uom || ""}</td>
                <td className="border p-2 text-right">{Number(r.total_part_value || 0).toFixed(2)}</td>
                <td className="border p-2">{r.batch_id || ""}</td>
                <td className="border p-2 text-center">{r.created_by || ""}</td>
              </tr>
            ))}
            {invRows.length === 0 && (
              <tr>
                <td className="border p-3 text-gray-600" colSpan={7}>
                  No issued inventory parts yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-right font-semibold mb-6">
        Total lines: {invRows.length} • Total qty: {invTotals.tq.toFixed(2)} • Total value: {invTotals.tv.toFixed(2)}
      </div>

      {/* Non-inventory section */}
      <div className="border rounded p-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="font-semibold">Non-Inventory Parts Transaction</div>
          <button
            className="ml-auto px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            type="button"
            onClick={() => setNonAddOpen(true)}
          >
            ➕ Add Non-Inv Part
          </button>
          <button
            className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            type="button"
            onClick={refreshNon}
            disabled={nonBusy}
          >
            {nonBusy ? "Loading…" : "Refresh"}
          </button>
        </div>

        {nonErr ? <div className="text-red-600 mt-2">{nonErr}</div> : null}
      </div>

      <div className="border rounded overflow-auto max-h-[40vh]">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {["When", "Description", "Qty", "UOM", "Unit Value", "Total Value", "By"].map((h) => (
                <th key={h} className="border p-2 bg-gray-100 text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nonRows.map((r, idx) => (
              <tr key={r.id ?? idx} className="hover:bg-gray-50">
                <td className="border p-2 whitespace-nowrap">{String(r.created_at || "").slice(0, 19)}</td>
                <td className="border p-2">{r.part_description || ""}</td>
                <td className="border p-2 text-center">{Number(r.part_quantity || 0).toFixed(2)}</td>
                <td className="border p-2 text-center">{r.uom || ""}</td>
                <td className="border p-2 text-right">{Number(r.part_value_ea || 0).toFixed(2)}</td>
                <td className="border p-2 text-right">{Number(r.total_value || 0).toFixed(2)}</td>
                <td className="border p-2 text-center">{r.created_by || ""}</td>
              </tr>
            ))}
            {nonRows.length === 0 && (
              <tr>
                <td className="border p-3 text-gray-600" colSpan={7}>
                  No non-inventory parts issued yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-right font-semibold mt-2">
        Total lines: {nonRows.length} • Total qty: {nonTotals.tq.toFixed(2)} • Total value: {nonTotals.tv.toFixed(2)}
      </div>

      {/* Search modal */}
      <PartsSearchIssueModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        site={site}
        onPick={(row) => {
          const p = row?.["Part Number"] || "";
          if (p) setPn(p);
          setSearchOpen(false);
        }}
      />

      {/* QR scan modal */}
      <QRScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScanned={(text) => {
          // Use full scanned text as PN, but trim whitespace
          setPn(String(text || "").trim());
          setScanOpen(false);
        }}
      />

      {/* Add Non-Inv modal */}
      <AddNonInvModal
        open={nonAddOpen}
        onClose={() => setNonAddOpen(false)}
        site={site}
        woNumber={woNumber}
        assetCode={assetCode || null}
        onSaved={async () => {
          setNonAddOpen(false);
          await refreshNon();
        }}
      />
    </div>
  );
}

/* =========================
 * Parts Search (reuse /api/pickers/parts)
 * ========================= */
function PartsSearchIssueModal({ open, onClose, site, onPick }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // filters
  const [partNumber, setPartNumber] = useState("");
  const [desc, setDesc] = useState("");
  const [cat, setCat] = useState("");
  const [mfg, setMfg] = useState("");
  const [loc, setLoc] = useState("");
  const [dept, setDept] = useState("");
  const [pj, setPj] = useState("");
  const [equip, setEquip] = useState("");
  const [asset, setAsset] = useState("");

  const [opt, setOpt] = useState({
    categories: [],
    manufacturers: [],
    locations: [],
    departments: [],
    project_jobs: [],
    equipment: [],
    asset_codes: [],
  });

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const qs = new URLSearchParams({ site });
        const r = await apiFetch(`/api/pickers/parts/options?${qs}`);
        const data = r.ok ? await r.json() : null;
        if (!alive) return;
        setOpt({
          categories: data?.categories || [],
          manufacturers: data?.manufacturers || [],
          locations: data?.locations || [],
          departments: data?.departments || [],
          project_jobs: data?.project_jobs || [],
          equipment: data?.equipment || [],
          asset_codes: data?.asset_codes || [],
        });
      } catch {
        if (!alive) return;
        setOpt({
          categories: [],
          manufacturers: [],
          locations: [],
          departments: [],
          project_jobs: [],
          equipment: [],
          asset_codes: [],
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, site]);

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        site,
        part_number: partNumber,
        description: desc,
        category: cat,
        manufacturer: mfg,
        location: loc,
        department: dept,
        project_job: pj,
        equipment: equip,
        asset_code: asset,
        limit: "200",
      });
      const r = await apiFetch(`/api/pickers/parts?${qs}`);
      if (!r.ok) throw new Error(await r.text());
      setRows((await r.json()) || []);
    } catch (e) {
      setErr(String(e.message || e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    // start empty; user hits Load
    setRows([]);
    setErr("");
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const clear = () => {
    setPartNumber("");
    setDesc("");
    setCat("");
    setMfg("");
    setLoc("");
    setDept("");
    setPj("");
    setEquip("");
    setAsset("");
    setRows([]);
  };

  return (
    <Modal title="Search Inventory (Issue to WO)" onClose={onClose}>
      {err && <div className="text-red-600 mb-2">{err}</div>}

      <div className="flex gap-2 mb-2">
        <button
          className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          type="button"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Loading…" : "Load Parts"}
        </button>
        <button className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300" type="button" onClick={clear}>
          Clear
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
        <input className="border rounded px-2 py-2" placeholder="Part Number" value={partNumber} onChange={(e) => setPartNumber(e.target.value)} />
        <input className="border rounded px-2 py-2 md:col-span-3" placeholder="Description" value={desc} onChange={(e) => setDesc(e.target.value)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <select className="border rounded px-2 py-2" value={dept} onChange={(e) => setDept(e.target.value)}>
          <option value="">All Departments</option>
          {opt.departments.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="border rounded px-2 py-2" value={pj} onChange={(e) => setPj(e.target.value)}>
          <option value="">All Project Jobs</option>
          {opt.project_jobs.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="border rounded px-2 py-2" value={equip} onChange={(e) => setEquip(e.target.value)}>
          <option value="">All Equipment</option>
          {opt.equipment.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="border rounded px-2 py-2" value={loc} onChange={(e) => setLoc(e.target.value)}>
          <option value="">All Locations</option>
          {opt.locations.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <select className="border rounded px-2 py-2" value={asset} onChange={(e) => setAsset(e.target.value)}>
          <option value="">All Asset Codes</option>
          {opt.asset_codes.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="border rounded px-2 py-2" value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">All Categories</option>
          {opt.categories.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="border rounded px-2 py-2" value={mfg} onChange={(e) => setMfg(e.target.value)}>
          <option value="">All Manufacturers</option>
          {opt.manufacturers.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      <div className="border rounded overflow-auto max-h-[55vh]">
        {rows.map((r) => (
          <button
            key={r["Part Number"]}
            className="w-full text-left px-3 py-2 border-b hover:bg-gray-50"
            type="button"
            onClick={() => onPick(r)}
          >
            <div className="font-mono">{r["Part Number"]}</div>
            <div className="text-sm text-gray-700">{r.Description || ""}</div>
            <div className="text-xs text-gray-600">
              On Hand: {r["Total Quantity"]} • Loc: {r["Location Display"] || r.Location || "-"} • UOM: {r["UOM Display"] || "-"}
            </div>
          </button>
        ))}
        {!loading && rows.length === 0 && (
          <div className="p-3 text-sm text-gray-600">
            Click <b>Load Parts</b> to fetch results (use filters first to reduce load).
          </div>
        )}
      </div>
    </Modal>
  );
}

/* =========================
 * QR Scanner Modal
 * - Uses BarcodeDetector if available
 * - Fallback: asks user to type/paste
 * ========================= */
function QRScanModal({ open, onClose, onScanned }) {
  const [err, setErr] = useState("");
  const [manual, setManual] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setManual("");
  }, [open]);

  if (!open) return null;

  const supportsBarcodeDetector = typeof window !== "undefined" && "BarcodeDetector" in window;

  return (
    <Modal title="Scan QR Code" onClose={onClose}>
      {err && <div className="text-red-600 mb-2">{err}</div>}

      {supportsBarcodeDetector ? (
        <BarcodeDetectorScanner
          onResult={(txt) => {
            if (txt) onScanned(txt);
          }}
          onError={(e) => setErr(String(e))}
        />
      ) : (
        <div className="text-sm text-gray-600 mb-3">
          This browser doesn’t support camera QR scanning. Paste or type the Part # below.
        </div>
      )}

      <div className="border rounded p-3 mt-3">
        <div className="font-semibold mb-2">Manual Entry (fallback)</div>
        <input
          className="border rounded px-2 py-2 w-full"
          placeholder="Paste scanned value…"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
        />
        <div className="mt-2 flex gap-2">
          <button
            className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            type="button"
            onClick={() => onScanned(manual)}
            disabled={!manual.trim()}
          >
            Use Value
          </button>
          <button className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

function BarcodeDetectorScanner({ onResult, onError }) {
  const videoRef = useState(() => ({ current: null }))[0];
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let stream = null;
    let raf = 0;
    let detector = null;

    const start = async () => {
      try {
        detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      } catch (e) {
        onError?.(e?.message || e);
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
      } catch (e) {
        onError?.("Camera permission denied or unavailable.");
        return;
      }

      if (!videoRef.current) {
        onError?.("Video element not ready.");
        return;
      }

      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setRunning(true);

      const tick = async () => {
        if (!videoRef.current || videoRef.current.readyState < 2) {
          raf = requestAnimationFrame(tick);
          return;
        }

        try {
          const codes = await detector.detect(videoRef.current);
          if (codes && codes.length) {
            const raw = codes[0]?.rawValue || "";
            if (raw) {
              onResult?.(raw);
              return;
            }
          }
        } catch {
          // ignore detect errors
        }

        raf = requestAnimationFrame(tick);
      };

      raf = requestAnimationFrame(tick);
    };

    start();

    return () => {
      cancelAnimationFrame(raf);
      setRunning(false);
      try {
        if (stream) stream.getTracks().forEach((t) => t.stop());
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="text-sm text-gray-600 mb-2">
        Point your camera at the QR code. It will auto-detect.
      </div>
      <div className="border rounded overflow-hidden">
        <video ref={(el) => (videoRef.current = el)} className="w-full" playsInline muted />
      </div>
      <div className="text-xs text-gray-500 mt-2">
        {running ? "Scanning…" : "Starting camera…"}
      </div>
    </div>
  );
}

/* =========================
 * Add Non-Inv Modal (simple)
 * ========================= */
function AddNonInvModal({ open, onClose, site, woNumber, assetCode, onSaved }) {
  const [desc, setDesc] = useState("");
  const [uom, setUom] = useState("");
  const [qty, setQty] = useState("1");
  const [unitCost, setUnitCost] = useState("0");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDesc("");
    setUom("");
    setQty("1");
    setUnitCost("0");
    setErr("");
    setBusy(false);
  }, [open]);

  if (!open) return null;

  const total = (Number(qty || 0) || 0) * (Number(unitCost || 0) || 0);

  const save = async () => {
    setErr("");
    const d = desc.trim();
    const q = Number(qty || 0) || 0;
    const uc = Number(unitCost || 0) || 0;

    if (!d) return setErr("Description required.");
    if (!(q > 0)) return setErr("Qty must be > 0.");
    if (uc < 0) return setErr("Unit cost must be >= 0.");

    setBusy(true);
    try {
      const r = await apiFetch("/api/wo/noninv/add", {
        method: "POST",
        body: JSON.stringify({
          site,
          wo_number: woNumber,
          part_description: d,
          part_quantity: q,
          uom: uom.trim() || null,
          part_value_ea: uc,
          asset_code: assetCode || null,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      await onSaved?.();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Add Non-Inventory Part — ${woNumber}`} onClose={onClose}>
      {err && <div className="text-red-600 mb-2">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <div className="text-xs text-gray-600 mb-1">Description</div>
          <input className="border rounded px-2 py-2 w-full" value={desc} onChange={(e) => setDesc(e.target.value)} />
        </div>

        <div>
          <div className="text-xs text-gray-600 mb-1">UOM</div>
          <input className="border rounded px-2 py-2 w-full" value={uom} onChange={(e) => setUom(e.target.value)} />
        </div>

        <div>
          <div className="text-xs text-gray-600 mb-1">Qty</div>
          <input className="border rounded px-2 py-2 w-full" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
        </div>

        <div>
          <div className="text-xs text-gray-600 mb-1">Unit Cost</div>
          <input className="border rounded px-2 py-2 w-full" inputMode="decimal" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} />
        </div>

        <div>
          <div className="text-xs text-gray-600 mb-1">Total</div>
          <div className="border rounded px-2 py-2 w-full bg-gray-50">{total.toFixed(2)}</div>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <button className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50" type="button" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Add"}
        </button>
        <button className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" type="button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

/* =========================
 * WO Cost Tab (wo_cost)
 * ========================= */
function WOCostTab({ site, woNumber }) {
  const [assetCode, setAssetCode] = useState("");
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // add form
  const [desc, setDesc] = useState("");
  const [cost, setCost] = useState("");

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  // Load asset_code once (for logging)
  useEffect(() => {
    if (!site || !woNumber) return;
    (async () => {
      try {
        const qs = new URLSearchParams({ site, wo_number: woNumber });
        const r = await apiFetch(`/api/wo/get?${qs}`);
        if (!r.ok) return;
        const wo = await r.json();
        setAssetCode((wo?.asset_code || "").trim());
      } catch {
        // ignore
      }
    })();
  }, [site, woNumber]);

  const load = async () => {
    setErr("");
    setBusy(true);
    try {
      const qs = new URLSearchParams({ site, wo_number: woNumber, limit: "500" });
      const r = await apiFetch(`/api/wo/cost/list?${qs}`);
      if (!r.ok) throw new Error(await r.text());
      setRows((await r.json()) || []);
    } catch (e) {
      setErr(String(e.message || e));
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!site || !woNumber) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, woNumber]);

  const totalCost = useMemo(() => {
    return rows.reduce((s, r) => s + (Number(r.cost || 0) || 0), 0);
  }, [rows]);

  const add = async () => {
    const d = (desc || "").trim();
    const c = Number(cost || 0);

    if (!d) return setErr("Please enter a cost description.");
    if (!(Number.isFinite(c))) return setErr("Please enter a valid numeric cost.");

    setErr("");
    setBusy(true);
    try {
      const r = await apiFetch("/api/wo/cost/add", {
        method: "POST",
        body: JSON.stringify({
          site,
          wo_number: woNumber,
          asset_code: assetCode || null,
          cost_description: d,
          cost: c,
        }),
      });
      if (!r.ok) throw new Error(await r.text());

      setDesc("");
      setCost("");
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="text-lg font-semibold">WO Cost</div>

        <button
          className="ml-auto px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          type="button"
          onClick={load}
          disabled={busy}
        >
          {busy ? "Loading…" : "Refresh"}
        </button>
      </div>

      {err && <div className="text-red-600 mb-2">{err}</div>}

      {/* Add Cost */}
      <div className="border rounded p-3 mb-3">
        <div className="font-semibold mb-2">Add Cost</div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
          <input
            className="border rounded px-2 py-2 md:col-span-8"
            placeholder="Description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
          />

          <input
            className="border rounded px-2 py-2 md:col-span-2"
            placeholder="Cost"
            inputMode="decimal"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
          />

          <button
            className="px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 md:col-span-2"
            type="button"
            onClick={add}
            disabled={busy}
          >
            ➕ Add Cost
          </button>
        </div>

        <div className="text-xs text-gray-500 mt-2">
          Tip: click a row below to edit (logs a reason to WO history).
        </div>
      </div>

      {/* Cost Table */}
      <div className="border rounded overflow-auto max-h-[55vh]">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {["Date", "Created By", "Description", "Cost"].map((h) => (
                <th key={h} className="border p-2 bg-gray-100 text-left">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr
                key={r.id ?? idx}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  setEditRow(r);
                  setEditOpen(true);
                }}
              >
                <td className="border p-2 whitespace-nowrap">{String(r.created_at || "").slice(0, 19)}</td>
                <td className="border p-2">{r.created_by || ""}</td>
                <td className="border p-2">{r.cost_description || ""}</td>
                <td className="border p-2 text-right">${Number(r.cost || 0).toFixed(2)}</td>
              </tr>
            ))}

            {rows.length === 0 && (
              <tr>
                <td className="border p-3 text-gray-600" colSpan={4}>
                  No WO cost entries yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-right font-semibold">
        Total WO Cost: ${totalCost.toFixed(2)}
      </div>

      <EditCostModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        site={site}
        woNumber={woNumber}
        assetCode={assetCode || null}
        row={editRow}
        onSaved={async () => {
          setEditOpen(false);
          await load();
        }}
      />
    </div>
  );
}

/* =========================
 * Edit Cost Modal (reason + confirm)
 * ========================= */
function EditCostModal({ open, onClose, site, woNumber, assetCode, row, onSaved }) {
  const [desc, setDesc] = useState("");
  const [cost, setCost] = useState("");
  const [reason, setReason] = useState("Cost error");
  const [other, setOther] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr("");
    setBusy(false);
    setReason("Cost error");
    setOther("");
    setDesc((row?.cost_description || "").toString());
    setCost(row?.cost != null ? String(row.cost) : "");
  }, [open, row]);

  if (!open) return null;
  if (!row?.id) return null;

  const save = async () => {
    setErr("");

    const d = (desc || "").trim();
    const c = Number(cost || 0);

    if (!d) return setErr("Description is required.");
    if (!Number.isFinite(c)) return setErr("Cost must be a valid number.");

    const reasonText = reason === "Others" && other.trim() ? `Reason: Others — ${other.trim()}` : `Reason: ${reason}`;
    const changeText = `Change: From '${row.cost_description || ""}' (${row.cost}) to '${d}' (${c.toFixed(2)})`;
    const confirmMsg = `${reasonText}\n\n${changeText}\n\nDo you want to continue?`;

    if (!window.confirm(confirmMsg)) return;

    setBusy(true);
    try {
      const r = await apiFetch("/api/wo/cost/edit", {
        method: "POST",
        body: JSON.stringify({
          site,
          wo_number: woNumber,
          asset_code: assetCode || null,
          id: row.id,
          cost_description: d,
          cost: c,
          reason,
          other_reason: reason === "Others" ? (other || "").trim() : "",
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      await onSaved?.();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Edit Cost" onClose={onClose}>
      {err && <div className="text-red-600 mb-2">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <div className="text-xs text-gray-600 mb-1">Description</div>
          <input className="border rounded px-2 py-2 w-full" value={desc} onChange={(e) => setDesc(e.target.value)} />
        </div>

        <div>
          <div className="text-xs text-gray-600 mb-1">Cost</div>
          <input className="border rounded px-2 py-2 w-full" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} />
        </div>

        <div>
          <div className="text-xs text-gray-600 mb-1">Reason for edit</div>
          <select className="border rounded px-2 py-2 w-full" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option>Cost error</option>
            <option>Description error</option>
            <option>Others</option>
          </select>
        </div>

        {reason === "Others" && (
          <div className="md:col-span-2">
            <div className="text-xs text-gray-600 mb-1">Explain change</div>
            <input className="border rounded px-2 py-2 w-full" value={other} onChange={(e) => setOther(e.target.value)} />
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-4">
        <button
          className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          type="button"
          onClick={save}
          disabled={busy}
        >
          {busy ? "Saving…" : "Update"}
        </button>

        <button className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" type="button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}


/* =========================
 * Modal wrapper
 * ========================= */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-xl w-[min(1100px,95vw)] max-h-[90vh] overflow-hidden shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold">{title}</div>
          <button className="px-3 py-1 rounded bg-gray-200" onClick={onClose} type="button">
            Close
          </button>
        </div>
        <div className="p-4 overflow-auto max-h-[80vh]">{children}</div>
      </div>
    </div>
  );
}
